import React from "react";
import {
  Card,
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  Tooltip,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import * as MuiIcons from "@mui/icons-material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import PublicIcon from "@mui/icons-material/Public";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BuildIcon from "@mui/icons-material/Build";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import RoomPreferencesIcon from "@mui/icons-material/RoomPreferences";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import AssignmentIcon from "@mui/icons-material/Assignment";

const WorkOrderCard = ({ order, getAssignedUserName, getTypeName, getStatusInfo }) => {
  const theme = useTheme();
  const statusInfo = getStatusInfo(order.status);
  const IconComponent = statusInfo?.icon && MuiIcons[statusInfo.icon];

  const assignees = (() => {
    if (Array.isArray(order?.assignees_details) && order.assignees_details.length) {
      return order.assignees_details.map((a) => ({
        id: a.user_id,
        name: `${a.name || ""} ${a.lastname || ""}`.trim(),
        isPrimary: !!a.is_primary,
      }));
    }

    if (Array.isArray(order?.assignees) && order.assignees.length) {
      return order.assignees.map((id, idx) => ({
        id,
        name: getAssignedUserName ? getAssignedUserName(id) || `#${id}` : `#${id}`,
        isPrimary: idx === 0,
      }));
    }

    if (order?.assign_to != null) {
      const id = order.assign_to;
      const name = getAssignedUserName ? getAssignedUserName(id) || `#${id}` : `#${id}`;
      return [{ id, name, isPrimary: true }];
    }

    return [];
  })();

  const initialsOf = (full) =>
    (full || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() || "")
      .join("") || "?";

  const borderColor =
    theme.palette[statusInfo?.color]?.main || statusInfo?.color || "#d0d7e2";

  const infoRowSx = {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
  };

  const iconSx = {
    mt: "2px",
    color: "#5f6b7a",
    fontSize: 18,
  };

  return (
    <Card
      sx={{
        borderRadius: "18px",
        border: "1px solid rgba(47,99,173,0.08)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        backgroundColor: "rgba(255,255,255,0.96)",
        overflow: "hidden",
        position: "relative",
        height: "100%",
      }}
    >
      <Box
        sx={{
          height: 5,
          backgroundColor: borderColor,
        }}
      />

      <Box sx={{ p: 2.25 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <AssignmentIcon sx={{ color: "#2f63ad", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  lineHeight: 1.2,
                }}
              >
                WO-{order.order_id}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Work order details and assignment overview
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {IconComponent && (
              <IconComponent
                fontSize="small"
                sx={{ color: borderColor }}
              />
            )}
            <Chip
              label={statusInfo?.statusname || "Unknown"}
              size="small"
              sx={{
                fontWeight: 600,
                borderRadius: "999px",
                backgroundColor: "rgba(47,99,173,0.08)",
                color: "#223142",
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2.5,
          }}
        >
          {/* LEFT COLUMN */}
          <Stack spacing={1.25}>
            <Box sx={infoRowSx}>
              <HomeIcon sx={iconSx} />
              <Typography variant="body2" sx={{ color: "#223142" }}>
                <strong>Property ID:</strong> {order.property_id || "N/A"}
              </Typography>
            </Box>

            <Box sx={infoRowSx}>
              <HomeIcon sx={iconSx} />
              <Typography variant="body2" sx={{ color: "#223142" }}>
                <strong>Member Name:</strong> {order.fullname || "N/A"}
              </Typography>
            </Box>

            <Box sx={infoRowSx}>
              <LocationOnIcon sx={iconSx} />
              <Typography variant="body2" sx={{ color: "#223142" }}>
                <strong>Address:</strong> {order.full_address || "N/A"}
              </Typography>
            </Box>

            <Box sx={infoRowSx}>
              <PublicIcon sx={iconSx} />
              <Typography variant="body2" sx={{ color: "#223142" }}>
                <strong>Membership Type:</strong> {order.ClubMembershipTypeCode || "N/A"}
              </Typography>
            </Box>

            <Box sx={infoRowSx}>
              <FlashOnIcon sx={iconSx} />
              <Typography variant="body2" sx={{ color: "#223142" }}>
                <strong>Priority:</strong> {order.priority || "N/A"}
              </Typography>
            </Box>

            <Box sx={infoRowSx}>
              <CalendarTodayIcon sx={iconSx} />
              <Typography variant="body2" sx={{ color: "#223142" }}>
                <strong>Created:</strong>{" "}
                {order.created_at
                  ? new Date(order.created_at).toLocaleDateString()
                  : "N/A"}
              </Typography>
            </Box>

            <Box sx={{ ...infoRowSx, mt: 0.25 }}>
              <PersonIcon sx={iconSx} />
              {assignees.length ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, width: "100%" }}>
                  <Typography variant="body2" sx={{ color: "#223142" }}>
                    <strong>Assigned To:</strong>
                  </Typography>

                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    {assignees.map((a) => {
                      const label = a.name || `#${a.id}`;
                      return (
                        <Tooltip
                          key={a.id}
                          title={a.isPrimary ? `${label} (Primary)` : label}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={label}
                            avatar={
                              <Avatar
                                sx={{
                                  width: 22,
                                  height: 22,
                                  fontSize: 11,
                                  bgcolor: a.isPrimary ? "primary.main" : "grey.300",
                                  color: a.isPrimary
                                    ? "primary.contrastText"
                                    : "text.primary",
                                }}
                              >
                                {initialsOf(label)}
                              </Avatar>
                            }
                            icon={a.isPrimary ? <StarIcon fontSize="small" /> : undefined}
                            sx={{
                              borderRadius: "999px",
                              maxWidth: "100%",
                              "& .MuiChip-icon": { ml: 0.25 },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Chip
                  label="Not Assigned"
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: "999px" }}
                />
              )}
            </Box>

            <Box sx={{ pt: 0.5 }}>
              <Button
                component={Link}
                to={`${order.order_id}`}
                state={{ from: "/home/workorders" }}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  borderColor: "rgba(47,99,173,0.18)",
                  color: "#223142",
                  "&:hover": {
                    borderColor: "#2f63ad",
                    color: "#2f63ad",
                    backgroundColor: "rgba(47,99,173,0.04)",
                  },
                }}
              >
                View Details
              </Button>
            </Box>
          </Stack>

          {/* RIGHT COLUMN */}
          <Stack spacing={1.25}>
            <Box sx={infoRowSx}>
              <BuildIcon sx={iconSx} />
              <Typography variant="body2" sx={{ color: "#223142" }}>
                <strong>Type:</strong> {getTypeName(order.workorder_type_id) || "N/A"}
              </Typography>
            </Box>

            <Box sx={infoRowSx}>
              <WaterDropIcon sx={iconSx} />
              <Typography variant="body2" sx={{ color: "#223142" }}>
                <strong>Subtype:</strong> {order.subtype_name || "N/A"}
              </Typography>
            </Box>

            <Box sx={infoRowSx}>
              <RoomPreferencesIcon sx={iconSx} />
              <Typography variant="body2" sx={{ color: "#223142" }}>
                <strong>Location:</strong> {order.location_name || "N/A"}
              </Typography>
            </Box>

            <Box sx={infoRowSx}>
              <DescriptionIcon sx={iconSx} />
              <Typography
                variant="body2"
                sx={{
                  color: "#223142",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.6,
                }}
              >
                <strong>Description:</strong> {order.description || "N/A"}
              </Typography>
            </Box>

          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

export default WorkOrderCard;